import { OBJECT_TYPES } from '../../Objects/types'
import { EVENTS, trackEvent } from '../../../events'

const SUPPORTED_EXPORT_TYPES = [
    'InsightElement',
    'SheetletElement',
    'Worksheet',
    'ProcessDiagramElement',
    'TextEditor',
]

export const isIntersecting = (frame, child) => {
    if (child.type === OBJECT_TYPES.ARROW) {
        const minX = Math.min(child.startInitialPosition.x, child.endInitialPosition.x)
        const minY = Math.min(child.startInitialPosition.y, child.endInitialPosition.y)
        const maxX = Math.max(child.startInitialPosition.x, child.endInitialPosition.x)
        const maxY = Math.max(child.startInitialPosition.y, child.endInitialPosition.y)
        const arrowAABB = {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
        }
        const horizontalOverlap = Math.max(
            0,
            Math.min(arrowAABB.x + arrowAABB.width, frame.x + frame.width) -
                Math.max(arrowAABB.x, frame.x)
        )
        const verticalOverlap = Math.max(
            0,
            Math.min(arrowAABB.y + arrowAABB.height, frame.y + frame.height) -
                Math.max(arrowAABB.y, frame.y)
        )
        return horizontalOverlap > 0 && verticalOverlap > 0
    } else {
        const horizontalOverlap = Math.max(
            0,
            Math.min(child.x + child.width, frame.x + frame.width) - Math.max(child.x, frame.x)
        )
        const verticalOverlap = Math.max(
            0,
            Math.min(child.y + child.height, frame.y + frame.height) - Math.max(child.y, frame.y)
        )
        return horizontalOverlap > 0 && verticalOverlap > 0
    }
}

const getFrameDimensions = (frame, screen) => {
    const frameWidth = frame.width
    const frameHeight = frame.height
    const screenWidth = screen.width
    const screenHeight = screen.height
    const frameAspectRatio = frameWidth / frameHeight
    const screenAspectRatio = screenWidth / screenHeight
    // screen width > height
    if (screenAspectRatio > 1) {
        if (frameAspectRatio > 1) {
            if ((screenHeight / frameHeight) * frameWidth > screenWidth) {
                return {
                    width: screenWidth,
                    height: (screenWidth / frameWidth) * frameHeight,
                }
            } else {
                return {
                    width: (screenHeight / frameHeight) * frameWidth,
                    height: screenHeight,
                }
            }
        } else if (frameAspectRatio < 1) {
            return {
                width: (frameHeight / screenHeight) * frameWidth,
                height: screenHeight,
            }
        } else {
            return {
                width: (frameHeight / screenHeight) * frameWidth,
                height: screenHeight,
            }
        }
        // screen height > width
    } else if (screenAspectRatio < 1) {
        if (frameAspectRatio > 1) {
            return {
                width: screenWidth,
                height: (screenWidth / frameWidth) * frameHeight,
            }
        } else if (frameAspectRatio < 1) {
            if ((screenWidth / frameWidth) * frameHeight > screenHeight) {
                return {
                    width: (screenHeight / frameHeight) * frameWidth,
                    height: screenHeight,
                }
            } else {
                return {
                    width: screenWidth,
                    height: (screenWidth / frameWidth) * frameHeight,
                }
            }
        } else {
            return {
                width: screenWidth,
                height: (screenWidth / frameWidth) * frameHeight,
            }
        }
        // square screen
    } else {
        if (frameAspectRatio > 1) {
            return {
                width: screenWidth,
                height: (screenWidth / frameWidth) * frameHeight,
            }
        } else if (frameAspectRatio < 1) {
            return {
                width: (screenHeight / frameHeight) * frameWidth,
                height: screenHeight,
            }
        } else {
            return {
                width: (screenHeight / frameHeight) * frameWidth,
                height: screenHeight,
            }
        }
    }
}

const getSlideIDFromFrameID = (frameID) => {
    if (!(typeof frameID === 'string')) return frameID
    let index = frameID.indexOf(':')
    if (index > 0) {
        return frameID.substring(0, index)
    } else {
        return frameID
    }
}

const constructFrameSlides = (slidesIDs, objects) => {
    // Filter only the frames that have their id included in the passed slidesIDs array
    const frames = objects?.filter(
        (obj) => obj.type === OBJECT_TYPES.FRAME && slidesIDs.includes(obj.id)
    )
    const rest = objects?.filter((obj) => obj.type !== OBJECT_TYPES.FRAME)

    return frames
        ?.map((frame) => {
            const slideObjects = []
            rest.forEach((obj) => {
                if (isIntersecting(frame, obj) && SUPPORTED_EXPORT_TYPES.includes(obj.type)) {
                    slideObjects.push({
                        ...obj,
                        slideX: obj.x - frame.x,
                        slideY: obj.y - frame.y,
                    })
                }
            })

            // Get frame dimensions
            const frameDimensions = getFrameDimensions(frame, window.screen)

            return { frame, slideObjects, ...frameDimensions }
        })
        .sort((a, b) => a.frame.presentationIndex - b.frame.presentationIndex) // Sort based on presentation index
        .filter((slide) => !slide.frame.hidden) // Filter out hidden frames
}

const getAssetRoute = (o) => {
    switch (o.type) {
        case 'SheetletElement':
            return 'sheet'
        case 'InsightElement':
            return 'chart'
        case 'ProcessDiagramElement':
            return 'process'
        default:
            return 'generic'
    }
}

export const handleUploadSlidesV2 = (
    presentationID,
    selectedCheckboxes,
    toast,
    slidesModal,
    presentationFrames,
    objects,
    server,
    userID,
    workspaceID,
    canvasID,
    postThumbnailGenerator
) => {
    slidesModal.show({
        onCloseCallback: () =>
            toast.warning('Slide upload process will continue on the background.'),
    })
    let slidesIDs = []
    if (selectedCheckboxes.length > 0) {
        slidesIDs = selectedCheckboxes.map((frameID) => getSlideIDFromFrameID(frameID))
    } else {
        slidesIDs = presentationFrames.map((frame) => getSlideIDFromFrameID(frame.id))
    }
    const slides = constructFrameSlides(slidesIDs, objects)
    const screenshotObjects = slides.flatMap((s) =>
        s.slideObjects.map((o) => ({ ...o, frameID: s.frame.id }))
    )
    console.log('ScreenshotObjects', screenshotObjects)
    const promises = screenshotObjects.map((o) => {
        const prompts = objects
            .filter((obj) => obj.type === OBJECT_TYPES.PROMPT)
            .filter((prompt) =>
                prompt.promptProps?.objects.some((id) =>
                    id.includes(typeof o.id !== 'string' ? o.id.toString() : o.id)
                )
            )
            .map((prompt) => prompt.promptProps.prompt)
            .filter((prompt) => prompt !== null)
            .flat()
        const action = {
            userID: userID,
            workspaceID: workspaceID,
            prompts: prompts.length > 0 ? btoa(JSON.stringify(prompts)) : null,
            type: getAssetRoute(o),
            insightKey:
                o.type === 'SheetletElement'
                    ? o.content?.worksheetID
                    : o.type === 'InsightElement'
                      ? o.content?.insightKey
                      : o.id,
            insightID: `${o.id}`,
            canvasID: canvasID,
            canvaDesignID: canvasID,
            worksheetID: o.type === 'SheetletElement' ? o.content?.worksheetID : null,
            elementID: o.type === 'TextEditor' ? o.id.toString().replace(':', '-') : null,
            diagramName: o.type === 'ProcessDiagramElement' ? o.content?.processDiagram : null,
            isSlideScreenshot: true,
            objectDimensions: { height: o.height, width: o.width },
            frameID: `${o.frameID}`,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        return postThumbnailGenerator(action)
    })
    Promise.allSettled(promises).then(() => {
        const action = {
            action: 'pushContentToSlides',
            canvasID: canvasID,
        }
        slidesModal.hide()
        trackEvent(EVENTS.CANVAS.SYNC_TO_SLIDES, {
            presentationID,
            canvasID,
        })
        toast.success(
            'Slides uploaded successfully! Your Google Slides presentation is being updated.'
        )
        server.postData(action, () => {})
    })
}
